import { type UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

import { useSessionStore } from '~/stores/Session';

import { type InvalidateCardDisputeQueriesOptions, invalidateCardDisputeQueries } from './invalidateCardDisputeQueries';

interface SessionContextSnapshot {
  platformId?: string;
  isSandbox?: boolean;
}

/**
 * Wraps dispute mutation options so that caches are only invalidated when the platform and
 * live/sandbox mode are unchanged between mutation start and response. A late response from a
 * previous context must never refresh the current view; platform switches clear the cache, and
 * mode switches are isolated by the query key hash.
 */
export const useCardDisputeMutationWrapper = () => {
  const queryClient = useQueryClient();
  const platformId = useSessionStore((state) => state.currentPlatform?.id);
  const isSandbox = useSessionStore((state) => state.isSandbox);
  const contextAtStart = useRef<SessionContextSnapshot | undefined>(undefined);

  return useCallback(
    <TData, TError, TVariables, TContext>(
      options: UseMutationOptions<TData, TError, TVariables, TContext> = {} as UseMutationOptions<
        TData,
        TError,
        TVariables,
        TContext
      >,
      invalidationFor: (data: TData, variables: TVariables) => InvalidateCardDisputeQueriesOptions = () => ({})
    ): UseMutationOptions<TData, TError, TVariables, TContext> => ({
      ...options,
      onMutate: (variables, context) => {
        contextAtStart.current = { platformId, isSandbox };
        return options.onMutate?.(variables, context) as TContext | Promise<TContext>;
      },
      onSuccess: (data, variables, context, mutationContext) => {
        const unchanged =
          contextAtStart.current?.platformId === platformId && contextAtStart.current?.isSandbox === isSandbox;
        if (unchanged) {
          invalidateCardDisputeQueries(queryClient, invalidationFor(data, variables));
        }
        return options.onSuccess?.(data, variables, context, mutationContext);
      },
    }),
    [queryClient, platformId, isSandbox]
  );
};
